import { getProductsByCategoryId } from "@/services/category";
import { getMultiProductsRating } from "@/services/product/reviews";
import { getCookies } from "cookies-next";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";

const useInfiniteProducts = ({ products, categoryID }) => {
  const { handshake } = getCookies();
  const [allProducts, setAllProducts] = useState(products || []);

  const [pageIndex, setPageIndex] = useState(1);
  const [allFilterKeys, setAllFilterKeys] = useState("");
  const [allSortingKeys, setAllSortingKeys] = useState(
    "sortBy=price&sortDir=DESC"
  );
  const [moreProductsIsLoading, setMoreProductsIsLoading] = useState(false);

  const {events, asPath} = useRouter()

  const { cache, mutate } = useSWRConfig();

  const res = useSWRMutation(
    `/api/category/pages/getMoreProducts?pageIdx=${pageIndex}&categoryID=${categoryID}&${allSortingKeys}&${allFilterKeys}`,
    async (_, { arg }) => {
      return await getProductsByCategoryId(
        handshake,
        categoryID,
        arg?.pageSize,
        arg?.pageNo,
        arg?.filterKeys,
        arg?.sortKeys
      );
    },
    {
      revalidateOnMount: 30 * 60 * 1000, // 30 minutes in milliseconds
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      populateCache: true,
    }
  );

  // initiate states on first load and when URL has changes
  useEffect(() => {
    setAllProducts(products);
    setMoreProductsIsLoading(false);
    setPageIndex(1);
    setAllFilterKeys("");
    setAllSortingKeys("sortBy=price&sortDir=DESC");
    events.on('routeChangeComplete', () => {
      console.log('mounted -------- ')
      setAllProducts(products);
      setMoreProductsIsLoading(false);
      setPageIndex(1);
      setAllFilterKeys("");
      setAllSortingKeys("sortBy=price&sortDir=DESC");
    })
  }, [asPath, events, products]);

  const handleGettingProductsAndCaching = useCallback(
    async (url_cach) => {
      setMoreProductsIsLoading(true);
      setPageIndex((prev) => prev + 1);

      let resResult = await res?.trigger({
        pageNo: pageIndex,
        pageSize: 30,
        categoryID,
        filterKeys: allFilterKeys,
        sortKeys: allSortingKeys,
      });

      const result = resResult?.data?.data || {};

      mutate({
        [url_cach]: result,
      });

      if (result?.products != undefined && Array.isArray(result?.products)) {
        const skus = [
          ...new Set(result?.products?.map((product) => product?.sku)),
        ];

        const ratings = await getMultiProductsRating(skus?.flat(), handshake);

        const enhancedProducts = result?.products.map((product) => ({
          ...product,
          rating: Object.entries(ratings?.data?.data?.at(1))
            ?.find((r) => r?.at(0) === product?.sku)
            ?.at(1) || { count: "0", rating: "0.0" },
        }));
        setAllProducts((prev) => [...prev, ...enhancedProducts]);
        if (result?.products?.length < 30) {
          setMoreProductsIsLoading(true);
        } else {
          setMoreProductsIsLoading(false);
        }
      }
    },
    [
      allFilterKeys,
      allSortingKeys,
      categoryID,
      handshake,
      mutate,
      pageIndex,
      res,
    ]
  );

  useEffect(() => {
    if (!moreProductsIsLoading && !res?.isMutating) {
      const target = document.getElementById("afterProductsSection");
      const url_cach = `/api/category/pages/getMoreProducts?pageIdx=${pageIndex}&categoryID=${categoryID}&${allSortingKeys}&${allFilterKeys}`;
      const observer = new IntersectionObserver(
        async (entries) => {
          if (entries[0].isIntersecting) {
            const cachedProducts = cache.get(url_cach)?.data?.data?.data?.products

            if (!(pageIndex > res?.data?.data?.data?.pages) && !cache.get(url_cach)) {
              // getting products for this category if this category not have data in the cach and page index stil smaller than all pages in the backend
              handleGettingProductsAndCaching(url_cach);
            } else {
              // if there are data in the cach
              if (
                cache &&
                cachedProducts &&
                cachedProducts?.length
              ) {
                // get products from the cach
                setPageIndex((prev) => prev + 1);
                setAllProducts((prev) => [
                  ...prev,
                  ...(cachedProducts || []),
                ]);
              } else {
                // if this category not have data in the cach
                handleGettingProductsAndCaching(url_cach);
              }
            }
          }
        },
        { threshold: 1.0 }
      );
      target && observer.observe(target);

      return () => observer.disconnect();
    }
  }, [pageIndex, moreProductsIsLoading, cache, mutate, res, categoryID, handleGettingProductsAndCaching, allSortingKeys, allFilterKeys]);

  return {
    allProducts,
    setAllProducts,
    setAllFilterKeys,
    allFilterKeys,
    allSortingKeys,
    setAllSortingKeys,
  };
};

export default useInfiniteProducts;
